import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {Link, withRouter} from 'react-router';
import { compose } from 'redux';
import { faDownload } from '@fortawesome/pro-solid-svg-icons/faDownload';
import { faSignOut } from '@fortawesome/pro-solid-svg-icons/faSignOut';

import {
  billingConfigs as billingConfigsDuck,
  customers as customersDuck,
  modals,
  schoolYears as schoolYearsDuck,
  grades,
  fields as fieldsDuck
} from 'ducks';

import { formatCurrency } from 'utils';
import {duckify, filterDuck, singleDuck} from 'utils/duckify';
import SaPropTypes from 'propTypes';

import PageHeader from 'components/PageHeader';
import Placeholder from 'components/Placeholder';
import BulkAction from 'components/ContactList/BulkActions/BulkAction';
import BulkActions from 'components/ContactList/BulkActions/BulkActions';
import Pagination from 'components/ContactList/Pagination';
import SchoolYearSelect from 'components/SchoolYearSelect';
import DateFilter from 'components/shared/DateFilter';
import TotalsHeader from 'components/shared/TotalsHeader';

import withFiltering from 'hocs/withFiltering';
import withPagination from 'hocs/withPagination';
import withSchoolYear, { defaultBillingSchoolYear } from 'hocs/withSchoolYear';
import withSorting from 'hocs/withSorting';

import BillingARReportList from './BillingARReportList';

import './styles/BillingARReport.scss';
import withDateFilter, { DATE_RANGES } from '../../hocs/withDateFilter';
import Filters from "../Filters/Filters";

// Load column definitions from JSON
const columns = require('../../../../config/ar_report_columns.json');

/** Columns to display for the totals summary */
const totalColumns = columns.filter(column => column.hideFromTotal !== true);
const columnHeaders = totalColumns.map(column => column.header);

const BillingARReport = ({
  downloadFile,
  location,
  onSchoolYearChange,
  onSort,
  page,
  setPage,
  schoolYears,
  schoolYearId,
  showModal,
  sort,
  dateFilter,
  filters,
  setFilters,
  grades,
  filterFields
}) => {
  const [ready, setReady] = useState(false);
  const [report, setReport] = useState({});
  const [error, setError] = useState(null);
  const {
    pages,
    rows = [],
    totals
  } = report;

  const searchParams = new URLSearchParams(location.search);
  const isArchived = year => year.archived
  const isSelected = year => year.id == schoolYearId
  const dropdownSchoolYears = schoolYears.filter(year => !isArchived(year) || isSelected(year))

  if (!searchParams.has('school_year_id')) {
    searchParams.set('school_year_id', schoolYearId);
  }

  for (const [key, value] of Object.entries(dateFilter && dateFilter.queryParams || {})) {
    if(value) {
      searchParams.set(key, value);
    }
  }

  // Queries report API when URL parameters change
  useEffect(
    () => {
      setReady(false);

      fetch(`/api/billing_ar_report?${searchParams}`, { credentials: 'include' })
        .then((response) => {
          setError(null);
          if (!response.ok) {
            return response.json().then(res => {
              throw Error(res.error);
            });
          };
          return response.json();
        })
        .then((json) => {
          setReport(json);
          setReady(true);
        })
        .catch(response => {
          if (response.message) {
            setError(response.message);
            setReady(true);
          } else {
            showModal('UnexpectedError');
          }
        });
    },
    [location.search]
  );

  const defaultFilterFields = [
    grades.ready && {
      solr_prompt: 'grade',
      display_name: 'Grade',
      options: grades.map(g => [g.name, g.id])
    },
  ];

  const columnValues = totals ? totalColumns.map(column => formatCurrency(totals[column.attribute])) : [];

  return (
    <div>
      <div className='billing-ar-report-header'>
        <PageHeader header='AR Report' inline />

        <SchoolYearSelect
          onSelect={(year) => onSchoolYearChange(year)}
          schoolYears={dropdownSchoolYears}
          schoolYearId={schoolYearId}
          withDefaultTerms
        />

        <BulkActions>
          <BulkAction title='Export' icon={faSignOut} disabled={rows.length === 0}>
            <BulkAction
              title='Download CSV'
              icon={faDownload}
              onClick={() => downloadFile(searchParams, 'csv')}
              disabled={rows.length === 0}
            />
            <BulkAction
              title='Download PDF'
              icon={faDownload}
              onClick={() => downloadFile(searchParams, 'pdf')}
              disabled={rows.length === 0}
            />
          </BulkAction>
        </BulkActions>

        <p>
          Your AR Report downloads will be available&nbsp;
          <Link to='/setup/user_jobs'>here</Link>
          .
        </p>
      </div>

      <Placeholder.loading ready={ready}>
        {error && <div className='alert alert-danger'>{error}</div>}

        <DateFilter useFutureOptions={false} dateFilter={dateFilter} />

        <TotalsHeader columnHeaders={columnHeaders} columnValues={columnValues} />

        <div className='billing-ar-space-between'>
          <Filters
            fields={defaultFilterFields.concat(filterFields)}
            selected={filters}
            submitFilters={setFilters}
            itemsName='terms'
          />
          <Pagination totalPages={pages} page={page} setPage={setPage} />
        </div>

        <BillingARReportList
          billingAccounts={rows}
          schoolYearId={schoolYearId}
          emptyMessage='There are no accounts.'
          onSort={onSort}
          sort={sort}
          showModal={showModal}
          searchParams={searchParams}
          dateFilter={dateFilter}
        />
      </Placeholder.loading>
    </div>
  );
};

BillingARReport.propTypes = {
  /** Used as the maximum selectable date when filtering */
  billingConfig: PropTypes.shape({
    test_effective_date: PropTypes.string
  }),

  /** Called when the "download" button is clicked */
  downloadFile: PropTypes.func,

  /** Used to tell when the page is ready for render */
  duckMeta: PropTypes.shape({
    billingConfig: PropTypes.shape({
      ready: PropTypes.bool
    })
  }),

  /** Used to read/manipulate the URL containing filters/sort info */
  location: PropTypes.shape({
    search: PropTypes.string
  }),

  /** School years for filtering */
  schoolYears: PropTypes.arrayOf(SaPropTypes.SchoolYear),

  /** The selected school year */
  schoolYearId: PropTypes.string,

  /** Called when a page number is clicked */
  setPage: PropTypes.func,

  /** Called when the school year selector is changed */
  onSchoolYearChange: PropTypes.func,

  /** Called to set a new sort criteria */
  onSort: PropTypes.func,

  /** Current page number */
  page: PropTypes.number,

  /** Displays a modal */
  showModal: PropTypes.func,

  /** Sort details */
  sort: PropTypes.shape({
    column: PropTypes.string,
    order: PropTypes.string
  })
};

BillingARReport.defaultProps = {
  billingConfig: {
    test_effective_date: null
  },
  duckMeta: {
    billingConfig: {
      ready: false
    }
  },
  location: {},
  page: 1,
  schoolYears: [],
  schoolYearId: '',
  sort: {
    column: 'name',
    order: 'desc'
  },

  downloadFile: () => false,
  onSchoolYearChange: () => false,
  onSort: () => false,
  setPage: () => false,
  showModal: () => false
};

const mapDuckToProps = () => ({
  billingConfig: singleDuck(billingConfigsDuck, 'current'),
  customer: singleDuck(customersDuck, 'current'),
  schoolYears: schoolYearsDuck,
  filterFields: filterDuck(fieldsDuck, {for: 'billing'}),
  grades
});

const mapDispatchToProps = (dispatch) => ({
  downloadFile: (searchParams, format) => dispatch(modals.actions.show('Downloader', {
    async: true,
    url: `/api/billing_ar_report/export.${format}?${searchParams}`,
    message: 'Your file is being prepared. You can safely close this modal and continue working. The report will automatically download when completed and will also be available on the User Jobs page.'
  })),
  showModal: (type) => dispatch(modals.actions.show(type))
});

export default compose(
  withRouter,
  withFiltering,
  withPagination,
  withSorting({ column: columns[0].sortName, order: 'asc' }),
  withDateFilter({ defaultRange: DATE_RANGES.last_30_days }),
  duckify(mapDuckToProps, () => ({}), mapDispatchToProps),
  withSchoolYear(defaultBillingSchoolYear)
)(BillingARReport);
